/**
 * Cache Service for optimizing performance on slow internet
 * Provides caching for API responses, images, and other data
 */

const CACHE_PREFIX = 'hr_cache_';
const DEFAULT_TTL = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

/**
 * Get cached data if it exists and is not expired
 * @param {string} key - Cache key
 * @param {number} ttl - Time to live in milliseconds (optional, defaults to 24 hours)
 * @returns {any|null} - Cached data or null if not found/expired
 */
export const getCachedData = (key, ttl = DEFAULT_TTL) => {
  try {
    const cacheKey = `${CACHE_PREFIX}${key}`;
    const cached = localStorage.getItem(cacheKey);
    
    if (!cached) {
      return null;
    }

    const { data, timestamp } = JSON.parse(cached);
    const now = Date.now();
    
    // Check if cache is expired
    if (now - timestamp > ttl) {
      localStorage.removeItem(cacheKey);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error reading from cache:', error);
    return null;
  }
};

/**
 * Set data in cache
 * @param {string} key - Cache key
 * @param {any} data - Data to cache
 * @param {number} ttl - Time to live in milliseconds (optional)
 */
export const setCachedData = (key, data, ttl = DEFAULT_TTL) => {
  try {
    const cacheKey = `${CACHE_PREFIX}${key}`;
    const cacheData = {
      data,
      timestamp: Date.now(),
      ttl
    };
    
    // Check localStorage size limit (usually 5-10MB)
    const dataString = JSON.stringify(cacheData);
    if (dataString.length > 5 * 1024 * 1024) {
      console.warn(`Cache data for key "${key}" is too large, skipping cache`);
      return;
    }
    
    localStorage.setItem(cacheKey, dataString);
  } catch (error) {
    // Handle quota exceeded error
    if (error.name === 'QuotaExceededError') {
      console.warn('LocalStorage quota exceeded, clearing old cache entries');
      clearOldCacheEntries();
      // Try again after clearing
      try {
        const cacheKey = `${CACHE_PREFIX}${key}`;
        const cacheData = {
          data,
          timestamp: Date.now(),
          ttl
        };
        localStorage.setItem(cacheKey, JSON.stringify(cacheData));
      } catch (retryError) {
        console.error('Failed to cache data after clearing old entries:', retryError);
      }
    } else {
      console.error('Error writing to cache:', error);
    }
  }
};

/**
 * Remove cached data
 * @param {string} key - Cache key
 */
export const removeCachedData = (key) => {
  try {
    const cacheKey = `${CACHE_PREFIX}${key}`;
    localStorage.removeItem(cacheKey);
  } catch (error) {
    console.error('Error removing from cache:', error);
  }
};

/**
 * Clear all cache entries
 */
export const clearAllCache = () => {
  try {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith(CACHE_PREFIX)) {
        localStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.error('Error clearing cache:', error);
  }
};

/**
 * Clear old cache entries (older than their TTL)
 */
export const clearOldCacheEntries = () => {
  try {
    const keys = Object.keys(localStorage);
    const now = Date.now();
    
    keys.forEach(key => {
      if (key.startsWith(CACHE_PREFIX)) {
        try {
          const cached = localStorage.getItem(key);
          if (cached) {
            const { timestamp, ttl } = JSON.parse(cached);
            if (now - timestamp > ttl) {
              localStorage.removeItem(key);
            }
          }
        } catch (error) {
          // If we can't parse, remove it
          localStorage.removeItem(key);
        }
      }
    });
  } catch (error) {
    console.error('Error clearing old cache entries:', error);
  }
};

/**
 * Get cache statistics
 * @returns {object} - Cache statistics
 */
export const getCacheStats = () => {
  try {
    const keys = Object.keys(localStorage);
    const cacheKeys = keys.filter(key => key.startsWith(CACHE_PREFIX));
    let totalSize = 0;
    let expiredCount = 0;
    const now = Date.now();

    cacheKeys.forEach(key => {
      try {
        const cached = localStorage.getItem(key);
        if (cached) {
          totalSize += cached.length;
          const { timestamp, ttl } = JSON.parse(cached);
          if (now - timestamp > ttl) {
            expiredCount++;
          }
        }
      } catch (error) {
        // Skip invalid entries
      }
    });

    return {
      totalEntries: cacheKeys.length,
      expiredEntries: expiredCount,
      totalSize: totalSize,
      totalSizeMB: (totalSize / (1024 * 1024)).toFixed(2)
    };
  } catch (error) {
    console.error('Error getting cache stats:', error);
    return { totalEntries: 0, expiredEntries: 0, totalSize: 0, totalSizeMB: '0.00' };
  }
};

// Cache keys constants
export const CACHE_KEYS = {
  HOTEL_IMAGE: (hotelId) => `hotel_image_${hotelId}`,
  HOTEL_DATA: (hotelId) => `hotel_data_${hotelId}`,
  ORDER_DATA: (orderId) => `order_data_${orderId}`,
  GEOLOCATION: (address) => `geolocation_${address}`,
  FUTURE_COMMISSION: (agentId) => `future_commission_${agentId}`,
};

// Initialize: clear old cache entries on load
if (typeof window !== 'undefined') {
  // Clear old entries every 5 minutes
  setInterval(clearOldCacheEntries, 5 * 60 * 1000);
  // Clear on page load
  clearOldCacheEntries();
}


